import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/LocaleContext';
import api from '../../services/api';
import {
  Car,
  ArrowRight,
  ChevronRight,
  Search,
  TrendingUp,
  Star,
  Award,
  Banknote,
  Phone,
} from 'lucide-react';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import StarfieldBackground from '../../components/ALLJsx/StarfieldBackground';
import {
  HERO_BACKDROP_VARIANT,
  getHeroBackdropTone,
  isDigitalHero,
} from '../../constants/starfieldVariants';
import MostViewedCarousel from '../../components/ALLJsx/MostViewedCarousel';
import UpcomingCarsCarousel from '../../components/ALLJsx/UpcomingCarsCarousel';
import { useEnsureCars } from '../../hooks/useEnsureCars';
import '../AllCss/Home.css';

function Home() {
  useEnsureCars();
  const navigate = useNavigate();
  const { t } = useLocale();
  const cars = useSelector((state) => state.cars.items);
  const [selectedBudget, setSelectedBudget] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [heroSlide, setHeroSlide] = useState(0);
  const [mostViewedCars, setMostViewedCars] = useState([]);
  const [upcomingCars, setUpcomingCars] = useState([]);
  const activeHeroBackdropVariant = HERO_BACKDROP_VARIANT;

  const heroSlides = [
    { title: t('home.heroTitle'), accent: t('home.heroTitleAccent') },
    { title: t('home.heroSlide2Title'), accent: t('home.heroSlide2Accent') },
    { title: t('home.heroSlide3Title'), accent: t('home.heroSlide3Accent') },
  ];
  const totalSlides = heroSlides.length;

  const makes = [...new Set(cars.map((car) => car.make))].sort();
  const bodyTypes = [...new Set(cars.map((car) => car.bodyType).filter(Boolean))].sort();
  const years = [...new Set(cars.map((car) => car.year))].sort((a, b) => b - a);

  const popularBrands = [...new Set(cars.map((car) => car.make))]
    .map((make) => ({
      make,
      count: cars.filter((car) => car.make === make).length,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
    .map((item) => item.make);

  const carsAvailable = cars.filter((c) => c.status === 'available').length;
  const carsSold = cars.filter((c) => c.status === 'sold' || c.status === 'Sold').length;
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const newArrivals = cars.filter((c) => {
    if (!c.createdAt) return false;
    const createdDate = new Date(c.createdAt);
    return createdDate >= thirtyDaysAgo && c.status === 'available';
  }).length;
  const uniqueBrands = [...new Set(cars.map((car) => car.make).filter(Boolean))].length;

  const heroShowcaseCars = cars
    .filter((c) => c.status === 'available' && c.images?.length > 0)
    .slice(0, 3);

  useEffect(() => {
    api.get('/cars/most-viewed?limit=4').then((r) => r.data.success && setMostViewedCars(r.data.data.cars)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/cars/recently-added?limit=8').then((r) => r.data.success && setUpcomingCars(r.data.data.cars)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  useEffect(() => {
    const nodes = document.querySelectorAll('.home-reveal:not(.home-reveal-visible)');
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('home-reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -32px 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [mostViewedCars.length, upcomingCars.length, popularBrands.length]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedBudget) {
      const [min, max] = selectedBudget.split('-');
      if (min) params.append('priceMin', min);
      if (max) params.append('priceMax', max);
    }
    if (selectedMake) params.append('make', selectedMake);
    if (selectedBodyType) params.append('bodyType', selectedBodyType);
    if (selectedYear) {
      params.append('yearMin', selectedYear);
      params.append('yearMax', selectedYear);
    }
    navigate(`/inventory${params.toString() ? `?${params}` : ''}`);
  };

  return (
    <div className="home-page home-redesign">
      <Navbar />

      {/* Hero: single search block, clean and centered, rotating tagline */}
      <section
        className={`home-hero${isDigitalHero(activeHeroBackdropVariant) ? ' home-hero--digital' : ''}`}
      >
        <div className="home-hero-backdrop" aria-hidden="true">
          <StarfieldBackground
            variant={activeHeroBackdropVariant}
            tone={getHeroBackdropTone(activeHeroBackdropVariant)}
            className="home-hero-starfield"
          />
          {heroShowcaseCars.map((car, index) => (
            <div
              key={car.id || `${car.stockNumber}-${index}`}
              className={`home-hero-showcase home-hero-showcase--${index + 1}`}
              style={{ backgroundImage: `url(${car.images[0]})` }}
            />
          ))}
        </div>
        <div className="home-hero-inner">
          <div className="home-hero-slide-wrap">
            <h1 className="home-hero-title" key={heroSlide}>
              {heroSlides[heroSlide].title} <span className="home-hero-accent">{heroSlides[heroSlide].accent}</span>
            </h1>
          </div>
          <div className="home-search-card">
            <div className="home-search-row">
              <div className="home-search-field">
                <label htmlFor="home-search-price">{t('home.price')}</label>
                <select id="home-search-price" value={selectedBudget} onChange={(e) => setSelectedBudget(e.target.value)}>
                  <option value="">{t('home.any')}</option>
                  <option value="0-15000">Under $15k</option>
                  <option value="15000-25000">$15k – $25k</option>
                  <option value="25000-35000">$25k – $35k</option>
                  <option value="35000-50000">$35k – $50k</option>
                  <option value="50000-">$50k+</option>
                </select>
              </div>
              <div className="home-search-field">
                <label htmlFor="home-search-make">{t('home.brand')}</label>
                <select id="home-search-make" value={selectedMake} onChange={(e) => setSelectedMake(e.target.value)}>
                  <option value="">{t('home.any')}</option>
                  {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="home-search-field">
                <label htmlFor="home-search-type">{t('home.type')}</label>
                <select id="home-search-type" value={selectedBodyType} onChange={(e) => setSelectedBodyType(e.target.value)}>
                  <option value="">{t('home.any')}</option>
                  {bodyTypes.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="home-search-field">
                <label htmlFor="home-search-year">{t('home.year')}</label>
                <select id="home-search-year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                  <option value="">{t('home.any')}</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button type="button" className="home-search-btn" onClick={handleSearch}>
                <Search size={18} /> {t('home.search')}
              </button>
            </div>
            <Link to="/inventory" className="home-browse-link">
              {t('home.browseInventory')} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="home-hero-actions">
            <Link to="/finance" className="home-hero-action">
              <Banknote size={16} />
              <span>{t('home.financingAvailable')}</span>
            </Link>
            <Link to="/sell/trade" className="home-hero-action">
              <Car size={16} />
              <span>{t('home.sellOrTrade')}</span>
            </Link>
            <Link to="/contact" className="home-hero-action">
              <Phone size={16} />
              <span>{t('home.contactUs')}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats bar: dark blue, icon + number + label (screenshot style) */}
      <section id="home-stats" className="home-stats-bar">
        <div className="home-stat-item">
          <div className="home-stat-row">
            <Car size={24} />
            <span className="home-stat-value">{carsAvailable.toLocaleString()}</span>
          </div>
          <span className="home-stat-label">{t('home.carsAvailable')}</span>
        </div>
        <div className="home-stat-item">
          <div className="home-stat-row">
            <TrendingUp size={24} />
            <span className="home-stat-value">{carsSold.toLocaleString()}</span>
          </div>
          <span className="home-stat-label">{t('home.carsSold')}</span>
        </div>
        <div className="home-stat-item">
          <div className="home-stat-row">
            <Star size={24} />
            <span className="home-stat-value">{newArrivals.toLocaleString()}</span>
          </div>
          <span className="home-stat-label">{t('home.newArrivals')}</span>
        </div>
        <div className="home-stat-item">
          <div className="home-stat-row">
            <Award size={24} />
            <span className="home-stat-value">{uniqueBrands.toLocaleString()}</span>
          </div>
          <span className="home-stat-label">{t('home.brands')}</span>
        </div>
      </section>

      {/* Featured cars */}
      <section className="home-section home-reveal">
        <MostViewedCarousel cars={mostViewedCars} title={t('home.mostViewedCars')} />
      </section>

      {/* Shop by type */}
      <section className="home-section home-reveal">
        <h2 className="home-section-title">{t('home.shopByType')}</h2>
        <div className="home-types">
          {['SUV', 'Sedan', 'Truck', 'Coupe', 'Hatchback', 'Convertible', 'Wagon', 'Electric'].map((type) => (
            <Link key={type} to={type === 'Electric' ? '/inventory?fuelType=electric' : `/inventory?bodyType=${type.toLowerCase()}`} className="home-type">
              {type} <ChevronRight size={16} />
            </Link>
          ))}
        </div>
      </section>

      {upcomingCars.length > 0 && (
        <section className="home-section home-section-alt home-reveal">
          <UpcomingCarsCarousel cars={upcomingCars} />
        </section>
      )}

      {/* Popular brands */}
      {popularBrands.length > 0 && (
        <section className="home-section home-section-alt home-reveal">
          <h2 className="home-section-title">{t('home.popularBrands')}</h2>
          <p className="home-section-sub">{t('home.trustedManufacturers')}</p>
          <div className="home-brands">
            {popularBrands.map((brand) => (
              <Link key={brand} to={`/inventory?make=${encodeURIComponent(brand)}`} className="home-brand">
                <span className="home-brand-name">{brand}</span>
                <span className="home-brand-count">{cars.filter((c) => c.make === brand && c.status === 'available').length} {t('home.available')}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTAs */}
      <section className="home-section home-reveal">
        <div className="home-ctas">
          <Link to="/finance" className="home-cta-card">
            <h3>{t('home.financingOptions')}</h3>
            <p>{t('home.preApprovalTerms')}</p>
          </Link>
          <Link to="/sell/trade" className="home-cta-card">
            <h3>{t('home.tradeInService')}</h3>
            <p>{t('home.tradeInDesc')}</p>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
